import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import type { ClusterListItem } from '@b4b/shared';
import { useAddMute, useSetRead, useSetSaved } from '../api/queries.js';
import { usePremium } from '../lib/usePremium.js';
import PremiumLock from './PremiumLock.js';

/** Words from the title worth muting (≥ 4 letters, deduplicated, max 6). */
export function muteCandidates(title: string): string[] {
  return [
    ...new Set(
      title
        .split(/\s+/)
        .map((w) => w.replace(/[^\p{L}\p{N}]/gu, ''))
        .filter((w) => w.length >= 4),
    ),
  ].slice(0, 6);
}

const rowClass =
  'block min-h-[44px] w-full rounded-lg px-3 py-2.5 text-left text-sm hover:bg-neutral-100 dark:hover:bg-neutral-800';

/** Shared ⋯ menu for list/grid cards: save, mark read, mute a title word.
 * Closes on outside tap or Escape. `dark` = render on a dark surface (over an image). */
export default function CardMenu({
  item,
  onClose,
  className = '',
}: {
  item: ClusterListItem;
  onClose: () => void;
  className?: string;
}) {
  const { t } = useTranslation();
  const setRead = useSetRead();
  const setSaved = useSetSaved();
  const addMute = useAddMute();
  const premium = usePremium();
  const tags = item.representative.tags.slice(0, 4);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <>
      {/* invisible backdrop: tap anywhere outside closes the menu without opening the card */}
      <div
        className="fixed inset-0 z-10"
        onClick={(e) => {
          e.stopPropagation();
          onClose();
        }}
        aria-hidden
      />
      <div
        role="menu"
        onClick={(e) => e.stopPropagation()}
        className={`absolute z-20 w-60 rounded-xl border border-neutral-200 bg-white p-2 shadow-lg dark:border-neutral-700 dark:bg-neutral-900 ${className}`}
      >
        <button
          role="menuitem"
          onClick={() => {
            setSaved.mutate({ id: item.id, saved: !item.saved });
            onClose();
          }}
          className={rowClass}
        >
          {item.saved ? '★ ' : '☆ '}
          {item.saved ? t('article.unsave') : t('article.save')}
        </button>
        <button
          role="menuitem"
          onClick={() => {
            setRead.mutate({ id: item.id, read: !item.read });
            onClose();
          }}
          className={rowClass}
        >
          ✓ {item.read ? t('article.markUnread') : t('article.markRead')}
        </button>
        <p className="mt-1 border-t border-neutral-200 px-3 pb-1 pt-2 text-xs font-semibold text-neutral-500 dark:border-neutral-700">
          {t('feed.mute')}
        </p>
        {muteCandidates(item.representative.title).map((word) => (
          <button
            key={word}
            role="menuitem"
            onClick={() => {
              addMute.mutate({ keyword: word });
              onClose();
            }}
            className={rowClass}
          >
            {t('feed.muteWord', { word })}
          </button>
        ))}
        {tags.length > 0 && (
          <>
            {/* tag mutes are premium: locked rows stay visible (a lock label, never an upsell modal) */}
            <p className="mt-1 flex items-center justify-between border-t border-neutral-200 px-3 pb-1 pt-2 text-xs font-semibold text-neutral-500 dark:border-neutral-700">
              {t('feed.muteTopic')}
              {!premium.active && <PremiumLock />}
            </p>
            {tags.map((tag) => (
              <button
                key={tag}
                role="menuitem"
                // aria-disabled, not `disabled`: the row stays focusable so screen
                // readers reach it and the lock label above it
                aria-disabled={!premium.active}
                onClick={() => {
                  if (!premium.active) return;
                  addMute.mutate({ keyword: tag, kind: 'tag' });
                  onClose();
                }}
                className={`${rowClass} ${premium.active ? '' : 'cursor-not-allowed opacity-50'}`}
              >
                #{tag}
              </button>
            ))}
          </>
        )}
      </div>
    </>
  );
}
